/** Plot colors are hex, not theme tokens: ECharts paints to canvas and can't read a CSS variable. */

export const PLOT_X_AXIS_ID = "plot-time";

/** The line under the plot. */
export const PLOT_BASELINE_COLOR = "#E7EAE8";

/** Gridlines, the same color as the baseline. */
export const GRID_LINE_COLOR = "#E7EAE8";

/** Axis tick labels. 4.9:1 on the card: this is the floor, don't lighten it. */
export const AXIS_LABEL_COLOR = "#6B7671";

/** Series stroke. Wider than the spec's 1.75 — 2 reads better against real data. */
export const SERIES_STROKE_WIDTH_PX = 2;

/** Lines and bars behind a focused stream. */
export const DIMMED_LINE_COLOR = "#d3d2cd";
export const DIMMED_LINE_WIDTH_PX = 1.1;
export const DIMMED_BAR_COLOR = "#dcdbd6";
/** ECharts' default z for lines and bars, and one above both. */
export const LINE_SERIES_Z = 3;
export const BAR_SERIES_Z = 2;
export const FOCUSED_SERIES_Z = 4;

/** Crosshair following the pointer, dashed 3 3. */
export const CROSSHAIR_HOVER_COLOR = "#9AA5A0";

/** Distance of a y-axis line from the plot grid edge — ECharts offset and gutter measurement both use this. */
export const Y_AXIS_BASE_OFFSET_PX = 10;

/** Matches --font-sans. Canvas Roboto was backed out once for rendering badly (#1837). */
export const CHART_FONT_FAMILY = "Roboto, sans-serif";

/** Y-axis tick label font size — must match Y_TICK_FONT. */
export const Y_TICK_FONT_SIZE_PX = 11;

/**
 * Must match the rendered style of the ECharts y-axis tick labels (axisLabel in
 * buildTimePanelOptions) — measured widths depend on it.
 */
export const Y_TICK_FONT = `400 ${Y_TICK_FONT_SIZE_PX}px ${CHART_FONT_FAMILY}`;

/**
 * Per-series value tags beside the crosshair dots (hover-values.tsx).
 */
export const HOVER_LABEL_FONT_SIZE_PX = 10;
export const HOVER_LABEL_RADIUS_PX = 10;
export const HOVER_LABEL_PADDING_X_PX = 4;
export const HOVER_LABEL_BORDER_PX = 1;

/** Value tag text. Never the series colour: the fill and border carry identity. */
export const HOVER_LABEL_TEXT_COLOR = "#111815";

export const HOVER_LABEL_FONT_WEIGHT = 600;
export const HOVER_LABEL_FONT = `${HOVER_LABEL_FONT_WEIGHT} ${HOVER_LABEL_FONT_SIZE_PX}px ${CHART_FONT_FAMILY}`;

/** Gap between the baseline and the x-axis tick labels. */
export const X_AXIS_TICK_LABEL_MARGIN_PX = 8;

export const X_AXIS_TICK_LABEL_FONT_SIZE_PX = 11;

/** Includes enough space below the baseline for letters such as "g" in month names. */
export const X_AXIS_TICK_LABEL_LINE_HEIGHT_PX = 12;

/** The band's span pill (band/band-span-pill.tsx): over the tick labels under a band below the plot, or above a band above it. */
export const SPAN_PILL_COLOR = "#1C2320";
export const SPAN_PILL_BACKGROUND = "#ffffff";
export const SPAN_PILL_SHADOW = "0 1px 3px rgba(28, 35, 32, 0.3)";
export const SPAN_PILL_FONT_SIZE_PX = 10;
export const SPAN_PILL_FONT_WEIGHT = 500;
export const SPAN_PILL_FONT = `${SPAN_PILL_FONT_WEIGHT} ${SPAN_PILL_FONT_SIZE_PX}px ${CHART_FONT_FAMILY}`;
/** Set explicitly so the pill is a whole number of pixels tall and stays sharp at 1x. */
export const SPAN_PILL_LINE_HEIGHT_PX = 12;
export const SPAN_PILL_RADIUS_PX = 10;
export const SPAN_PILL_PADDING_X_PX = 6;
/** Separate top and bottom: the digits sit optically low in the box, so the top carries one more. */
export const SPAN_PILL_PADDING_TOP_PX = 3;
export const SPAN_PILL_PADDING_BOTTOM_PX = 2;

/** The pill's height; a band above the plot reserves this plus PILL_ROW_GAP_PX over itself. */
export const SPAN_PILL_HEIGHT_PX = SPAN_PILL_LINE_HEIGHT_PX + SPAN_PILL_PADDING_TOP_PX + SPAN_PILL_PADDING_BOTTOM_PX;

/** Gap from the bottom of a band below the plot to the span pill. */
export const SPAN_PILL_MARGIN_PX = 6;

/** Space below the plot for the tick labels. */
export const X_AXIS_BAND_PX = X_AXIS_TICK_LABEL_MARGIN_PX + X_AXIS_TICK_LABEL_LINE_HEIGHT_PX;

/** Space between the highest labelled tick and the top of the plot. */
export const PLOT_HEADROOM_PX = 0;

/** Half the top tick label hangs above its gridline, so the plot starts this far down the canvas. */
export const TOP_TICK_LABEL_OVERHANG_PX = Math.ceil(Y_TICK_FONT_SIZE_PX / 2);

/** Space between header and the top of the plot. */
export const HEADER_TO_PLOT_PX = 8;
